import { alpha, Box, BoxProps } from '@mui/material';

interface Props extends BoxProps {
  maxHeight?: number | string;
}

/**
 * Scroll container that renders soft shadows at the top and bottom edges while
 * there is more content to scroll in that direction, hinting that the list is
 * scrollable. Uses the pure-CSS "scroll shadow" technique (layered background
 * gradients with mixed `background-attachment`), so it needs no scroll listeners.
 */
export function ScrollShadowBox({ children, maxHeight = 360, sx, ...rest }: Props) {
  return (
    <Box
      sx={[
        (theme) => {
          const background = theme.vars.palette.background.paper;
          const transparent = theme.vars
            ? `rgba(${theme.vars.palette.background.paperChannel} / 0)`
            : alpha(theme.palette.background.paper, 0);
          const transparentShadow = alpha(theme.palette.common.black, 0);
          const layers = (shadow: string) => `
              linear-gradient(${background} 30%, ${transparent}) center top,
              linear-gradient(${transparent}, ${background} 70%) center bottom,
              radial-gradient(farthest-side at 50% 0, ${shadow}, ${transparentShadow}) center top,
              radial-gradient(farthest-side at 50% 100%, ${shadow}, ${transparentShadow}) center bottom
            `;

          return {
            maxHeight,
            overflowY: 'auto',
            background: layers(alpha(theme.palette.common.black, 0.2)),
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% 32px, 100% 32px, 100% 12px, 100% 12px',
            backgroundAttachment: 'local, local, scroll, scroll',
            ...theme.applyStyles('dark', {
              background: layers(alpha(theme.palette.common.black, 0.6)),
            }),
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {children}
    </Box>
  );
}
